import { useEffect, useState } from 'react'
import { api } from '@/lib/api'
import { Eye, Check, X, RefreshCw, BarChart3 } from 'lucide-react'

interface BotInfo {
  id: string
  name: string
  mode: string
  model: string
}

interface ConvInfo {
  id: string
  lastMessagePreview: string | null
  channelKind: string
  clientStub: { displayName: string | null } | null
}

interface Suggestion {
  id: string
  botProfileId: string
  conversationId: string
  draftsJson: unknown
  selectedDraftIdx: number | null
  confidenceScore: string | number | null
  createdAt: string
  botProfile: BotInfo | null
  conversation: ConvInfo | null
}

interface PerBotStat {
  botProfileId: string
  botName: string
  totalReviewed: number
  correctCount: number
  correctRate: number
}

interface Stats {
  totalReviewed: number
  correctRate: number
  perBot: PerBotStat[]
}

function pickDraft(s: Suggestion): string {
  const arr = s.draftsJson
  if (!Array.isArray(arr)) return ''
  const idx = s.selectedDraftIdx ?? 0
  const item = arr[idx] ?? arr[0]
  if (typeof item === 'string') return item
  if (item && typeof item === 'object' && 'text' in item) {
    const t = (item as { text: unknown }).text
    return typeof t === 'string' ? t : ''
  }
  return ''
}

export function ShadowReviewPage() {
  const [items, setItems] = useState<Suggestion[]>([])
  const [stats, setStats] = useState<Stats | null>(null)
  const [loading, setLoading] = useState(true)
  const [err, setErr] = useState<string | null>(null)
  const [busyId, setBusyId] = useState<string | null>(null)
  const [comments, setComments] = useState<Record<string, string>>({})

  async function loadAll() {
    setErr(null)
    try {
      const [sugs, st] = await Promise.all([
        api<{ items: Suggestion[] }>('/v1/marketing/bots/shadow-suggestions'),
        api<Stats>('/v1/marketing/bots/shadow-reviews/stats'),
      ])
      setItems(sugs.items)
      setStats(st)
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e))
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    void loadAll()
  }, [])

  async function review(s: Suggestion, isCorrect: boolean) {
    setBusyId(s.id)
    try {
      await api('/v1/marketing/bots/shadow-reviews', {
        method: 'POST',
        body: JSON.stringify({
          suggestionId: s.id,
          isCorrect,
          comment: comments[s.id]?.trim() || undefined,
          // Sprint 8: reviewedByUserId desde JWT en el backend.
        }),
      })
      // Retirar de la lista local + refrescar stats.
      setItems((prev) => prev.filter((x) => x.id !== s.id))
      setComments((prev) => {
        const next = { ...prev }
        delete next[s.id]
        return next
      })
      try {
        const st = await api<Stats>('/v1/marketing/bots/shadow-reviews/stats')
        setStats(st)
      } catch {
        // si las stats fallan no rompemos el flujo
      }
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e))
    } finally {
      setBusyId(null)
    }
  }

  return (
    <div className="p-6 max-w-5xl">
      <header className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-lg font-medium tracking-tight flex items-center gap-2">
            <Eye className="size-5 text-blueprint-700" />
            Shadow review
          </h1>
          <p className="text-xs font-mono text-ink-700/60">
            Revisa las sugerencias de bots en modo SHADOW para medir precisión antes de
            promoverlos a SUGGESTIVE / AUTOPILOT
          </p>
        </div>
        <button type="button" className="bp-btn" onClick={() => void loadAll()} disabled={loading}>
          <RefreshCw className="size-3.5" />
          {loading ? 'Cargando…' : 'Refrescar'}
        </button>
      </header>

      {/* Stats */}
      {stats && (
        <div className="bp-card p-4 mb-4">
          <div className="flex items-center gap-2 mb-3">
            <BarChart3 className="size-4 text-blueprint-700" />
            <span className="text-xs font-mono uppercase tracking-wider text-ink-700/60">
              estadísticas globales
            </span>
          </div>
          <div className="grid grid-cols-3 gap-3 mb-3">
            <div className="bg-surface-alt/50 border border-ink-900/10 rounded px-3 py-2">
              <div className="text-[10px] font-mono uppercase text-ink-700/60">revisadas</div>
              <div className="text-lg font-medium">{stats.totalReviewed}</div>
            </div>
            <div className="bg-surface-alt/50 border border-ink-900/10 rounded px-3 py-2">
              <div className="text-[10px] font-mono uppercase text-ink-700/60">% correctas</div>
              <div className="text-lg font-medium">
                {(stats.correctRate * 100).toFixed(1)}%
              </div>
            </div>
            <div className="bg-surface-alt/50 border border-ink-900/10 rounded px-3 py-2">
              <div className="text-[10px] font-mono uppercase text-ink-700/60">pendientes</div>
              <div className="text-lg font-medium">{items.length}</div>
            </div>
          </div>
          {stats.perBot.length > 0 && (
            <div className="border-t border-ink-900/10 pt-2">
              <div className="text-[10px] font-mono uppercase tracking-wider text-ink-700/60 mb-1">
                por bot
              </div>
              <ul className="text-xs font-mono divide-y divide-ink-900/5">
                {stats.perBot.map((b) => (
                  <li key={b.botProfileId} className="py-1 flex justify-between">
                    <span className="text-ink-700">{b.botName}</span>
                    <span className="text-ink-700/70">
                      {(b.correctRate * 100).toFixed(0)}% ({b.correctCount}/{b.totalReviewed})
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {err && (
        <div className="mb-3 text-xs font-mono text-red-700 bg-red-50 border border-red-200 rounded px-3 py-2">
          {err}
        </div>
      )}

      {items.length === 0 && !loading && (
        <div className="bp-card p-10 text-center">
          <p className="text-sm text-ink-700/60">Sin sugerencias SHADOW pendientes de revisión.</p>
        </div>
      )}

      <ul className="space-y-3">
        {items.map((s) => {
          const draft = pickDraft(s)
          const confidence =
            s.confidenceScore === null
              ? null
              : typeof s.confidenceScore === 'string'
                ? Number(s.confidenceScore)
                : s.confidenceScore
          return (
            <li key={s.id} className="bp-card p-4 space-y-3">
              <div className="flex items-start justify-between gap-2">
                <div>
                  <div className="text-sm font-medium flex items-center gap-2">
                    {s.botProfile?.name ?? '(bot desconocido)'}
                    <span className="bp-pill bg-amber-50 text-amber-700">SHADOW</span>
                  </div>
                  <div className="text-[10px] font-mono text-ink-700/60">
                    {s.botProfile?.model ?? '—'} ·{' '}
                    {confidence !== null ? `confidence ${confidence.toFixed(2)}` : 'sin score'} ·{' '}
                    {s.conversation?.channelKind ?? ''}
                    {s.conversation?.clientStub?.displayName
                      ? ` · ${s.conversation.clientStub.displayName}`
                      : ''}
                  </div>
                </div>
                <div className="text-[10px] font-mono text-ink-700/50">
                  {new Date(s.createdAt).toLocaleString('es-ES')}
                </div>
              </div>

              {s.conversation?.lastMessagePreview && (
                <div>
                  <div className="text-[10px] font-mono uppercase tracking-wider text-ink-700/60 mb-1">
                    último mensaje del cliente
                  </div>
                  <div className="text-xs bg-surface-alt/50 border border-ink-900/10 rounded px-3 py-2 whitespace-pre-wrap">
                    {s.conversation.lastMessagePreview}
                  </div>
                </div>
              )}

              <div>
                <div className="text-[10px] font-mono uppercase tracking-wider text-ink-700/60 mb-1">
                  draft del bot
                </div>
                <div className="text-xs bg-blueprint-50/40 border border-blueprint-200/60 rounded px-3 py-2 whitespace-pre-wrap">
                  {draft || '(draft vacío)'}
                </div>
              </div>

              <label className="block">
                <span className="text-[10px] font-mono uppercase tracking-wider text-ink-700/60">
                  comentario opcional
                </span>
                <textarea
                  value={comments[s.id] ?? ''}
                  onChange={(e) =>
                    setComments((prev) => ({ ...prev, [s.id]: e.target.value }))
                  }
                  className="mt-1 w-full border border-ink-900/15 rounded px-2 py-1.5 text-xs"
                  rows={2}
                  placeholder="¿Por qué es correcta o incorrecta?"
                />
              </label>

              <div className="flex gap-2">
                <button
                  type="button"
                  className="bp-btn-primary"
                  onClick={() => void review(s, true)}
                  disabled={busyId === s.id}
                >
                  <Check className="size-3" /> Correcta
                </button>
                <button
                  type="button"
                  className="bp-btn text-red-700"
                  onClick={() => void review(s, false)}
                  disabled={busyId === s.id}
                >
                  <X className="size-3" /> Incorrecta
                </button>
              </div>
            </li>
          )
        })}
      </ul>
    </div>
  )
}
